// Analytics Tracking Utility with GDPR Consent
// Supports: Google Analytics 4, LinkedIn Insights Tag

const GA_MEASUREMENT_ID = "G-77W2PD9MKW";
const LINKEDIN_PARTNER_ID = "4868553";

declare global {
  interface Window {
    gtag: (
      command: "event" | "config" | "js" | "consent",
      eventName: string,
      params?: Record<string, unknown>
    ) => void;
    dataLayer: unknown[];
    _linkedin_data_partner_ids: string[];
    lintrk: ((a: string, b?: Record<string, unknown>) => void) & { q: unknown[][] };
  }
}

export type GAEventParams = {
  event_category?: string;
  event_label?: string;
  value?: number;
  page_location?: string;
  [key: string]: unknown;
};

let isInitialized = false;

// Initialize all analytics only after consent
export const initializeAnalytics = (): void => {
  if (typeof window === "undefined" || isInitialized) return;

  // Load Google Analytics
  const gaScript = document.createElement("script");
  gaScript.async = true;
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(gaScript);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag("js", new Date().toISOString());
  window.gtag("config", GA_MEASUREMENT_ID);

  // Load LinkedIn Insights Tag
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);

  if (!window.lintrk) {
    window.lintrk = function (a, b) {
      window.lintrk.q.push([a, b]);
    } as typeof window.lintrk;
    window.lintrk.q = [];
  }

  const liScript = document.createElement("script");
  liScript.type = "text/javascript";
  liScript.async = true;
  liScript.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
  document.head.appendChild(liScript);

  isInitialized = true;
};

// Check if analytics is ready
export const isAnalyticsReady = (): boolean => {
  return typeof window !== "undefined" && isInitialized && !!window.gtag;
};

export const trackEvent = (
  eventName: string,
  params?: GAEventParams
): void => {
  if (isAnalyticsReady()) {
    window.gtag("event", eventName, params);
  }
};

// Pre-defined tracking functions for common actions
export const trackButtonClick = (
  buttonName: string,
  location?: string
): void => {
  trackEvent("button_click", {
    event_category: "engagement",
    event_label: buttonName,
    button_location: location,
  });
};

export const trackNavClick = (linkName: string): void => {
  trackEvent("navigation_click", {
    event_category: "navigation",
    event_label: linkName,
  });
};

export const trackCTAClick = (ctaName: string, location?: string): void => {
  trackEvent("cta_click", {
    event_category: "conversion",
    event_label: ctaName,
    cta_location: location,
  });
};

export const trackCaseStudyView = (caseStudySlug: string): void => {
  trackEvent("case_study_view", {
    event_category: "content",
    event_label: caseStudySlug,
  });
};

export const trackInsightView = (insightSlug: string): void => {
  trackEvent("insight_view", {
    event_category: "content",
    event_label: insightSlug,
  });
};

export const trackFormSubmit = (formName: string): void => {
  trackEvent("form_submit", {
    event_category: "conversion",
    event_label: formName,
  });
};

export const trackChatInteraction = (action: string): void => {
  trackEvent("chat_interaction", {
    event_category: "engagement",
    event_label: action,
  });
};

export const trackExternalLink = (url: string): void => {
  trackEvent("outbound_click", {
    event_category: "engagement",
    event_label: url,
  });
};

export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  trackEvent("page_view", {
    page_location: pagePath,
    page_title: pageTitle,
  });
};

// ─── Homepage spec events ───

export const trackCallBookingStarted = (
  location: "hero" | "nav" | "mid_page" | "final" | "sticky-mobile"
): void => {
  trackEvent("call_booking_started", {
    event_category: "conversion",
    event_label: "Book a call",
    cta_location: location,
  });
};

export const trackCallBooked = (
  location?: "hero" | "nav" | "mid_page" | "final" | "sticky-mobile"
): void => {
  trackEvent("call_booked", {
    event_category: "conversion",
    event_label: "Call booked",
    cta_location: location,
  });
};


export const trackFaqOpened = (question: string): void => {
  trackEvent("faq_opened", {
    event_category: "engagement",
    event_label: question,
    question,
  });
};

export const trackScrollTerms = (): void => {
  trackEvent("scroll_terms", {
    event_category: "engagement",
    event_label: "Reached terms section",
  });
};

export const trackPhoneCall = (location?: string): void => {
  trackEvent("phone_call_click", {
    event_category: "conversion",
    event_label: "+44 (0) 203 488 3090",
    cta_location: location,
  });
};
